import type {
    BlockQuoteElement,
    CodeBlockElement,
    CodeInlineElement,
    Descendant,
    HeadingOneElement,
    HeadingThreeElement,
    HeadingTwoElement,
    HorizontalRuleElement,
    ImageElement,
    LinkElement,
    ListElement,
    ListItemElement,
    ListItemTextElement,
    MathBlockElement,
    MathInlineElement,
    ParagraphElement,
    SkeletonBlockElement,
    TableBodyElement,
    TableCellElement,
    TableElement,
    TableHeaderElement,
    TableRowElement,
} from "slate";

export const text = (text: string) => ({text});

export const bold = (text: string) => ({text, bold: true});

export const italic = (text: string) => ({text, italic: true});

export const underline = (text: string) => ({text, underline: true});

export const link = (url: string, ...children: Descendant[]): LinkElement => ({
    type: "link",
    url,
    children,
});

export const mathInline = (tex: string): MathInlineElement => ({
    type: "math-inline",
    tex,
    children: [text("")],
});

export const mathBlock = (tex: string): MathBlockElement => ({
    type: "math-block",
    tex,
    children: [text("")],
});

export const codeInline = (...children: Descendant[]): CodeInlineElement => ({
    type: "code-inline",
    children,
});

export const codeBlock = (
    language: string,
    ...children: Descendant[]
): CodeBlockElement => ({
    type: "code-block",
    language,
    children,
});

export const image = (url: string, altText: string = ""): ImageElement => ({
    type: "image",
    url,
    alt: altText,
    children: [text("")],
});

export const paragraph = (...children: Descendant[]): ParagraphElement => ({
    type: "paragraph",
    children,
});

export const headingOne = (...children: Descendant[]): HeadingOneElement => ({
    type: "heading-one",
    children,
});

export const headingTwo = (...children: Descendant[]): HeadingTwoElement => ({
    type: "heading-two",
    children,
});

export const headingThree = (
    ...children: Descendant[]
): HeadingThreeElement => ({
    type: "heading-three",
    children,
});

export const horizontalRule = (): HorizontalRuleElement => ({
    type: "horizontal-rule",
    children: [text("")],
});

export const bulletedList = (
    ...children: ListElement["children"]
): ListElement => ({
    type: "bulleted-list",
    children,
});

export const numberedList = (
    ...children: ListElement["children"]
): ListElement => ({
    type: "numbered-list",
    children,
});

export const letteredList = (
    ...children: ListElement["children"]
): ListElement => ({
    type: "lettered-list",
    children,
});

export const li = (
    ...children: ListItemElement["children"]
): ListItemElement => ({
    type: "list-item",
    children,
});

export const liText = (...children: Descendant[]): ListItemTextElement => ({
    type: "list-item-text",
    children,
});

export const blockQuote = (...children: Descendant[]): BlockQuoteElement => ({
    type: "block-quote",
    children,
});

export const table = (...children: TableElement["children"]): TableElement => ({
    type: "table",
    children,
});

export const tableHeader = (
    ...children: TableHeaderElement["children"]
): TableHeaderElement => ({
    type: "table-header",
    children,
});

export const tableBody = (
    ...children: TableBodyElement["children"]
): TableBodyElement => ({
    type: "table-body",
    children,
});

export const tableRow = (
    ...children: TableRowElement["children"]
): TableRowElement => ({
    type: "table-row",
    children,
});

export const tableCell = (...children: Descendant[]): TableCellElement => ({
    type: "table-cell",
    children,
});

export const skeletonBlock = (
    placeholderKey: string,
): SkeletonBlockElement => ({
    type: "skeleton-block",
    placeholderKey,
    children: [text("")],
});
